<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="md:flex md:items-center md:justify-between">
      <div class="flex-1">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{{ company.name }}</h1>
        <div class="mt-2 flex items-center space-x-3">
          <span
            :class="getStatusBadgeClass(company.company_status)"
            class="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full"
          >
            {{ formatStatus(company.company_status) }}
          </span>
          <span
            v-if="!company.is_active"
            class="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
          >
            Inactive
          </span>
        </div>
      </div>
      <div class="mt-4 flex space-x-3 md:mt-0 md:ml-4">
        <Link
          :href="route('site-owner.companies.edit', company.id)"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PencilIcon class="h-5 w-5 mr-2" />
          Edit Company
        </Link>
        <Link
          :href="route('site-owner.companies.impersonate', company.id)"
          method="post"
          as="button"
          class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
        >
          <UserIcon class="h-5 w-5 mr-2" />
          Impersonate
        </Link>
        <Link
          :href="route('site-owner.companies.index')"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Back to List
        </Link>
      </div>
    </div>

    <!-- Company Information -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Basic Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Basic Information
          </h3>
        </div>
        <div class="px-6 py-4">
          <dl class="space-y-4">
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Company Name</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.name }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Tenant Database</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white font-mono">{{ company.tenant_db_name }}</dd>
            </div>
            <div v-if="company.website">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Website</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                <a :href="company.website" target="_blank" class="text-blue-600 dark:text-blue-400 hover:underline">
                  {{ company.website }}
                </a>
              </dd>
            </div>
            <div v-if="company.phone">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.phone }}</dd>
            </div>
            <div v-if="company.industry_type">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Industry</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.industry_type }}</dd>
            </div>
            <div v-if="company.company_size">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Company Size</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.company_size }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ formatDate(company.created_at) }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Address Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Address
          </h3>
        </div>
        <div class="px-6 py-4">
          <dl class="space-y-4">
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Street Address</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ company.address_line_1 }}
                <span v-if="company.address_line_2"><br>{{ company.address_line_2 }}</span>
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">City</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.city }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">State / Province</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.state_province }}</dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Postal Code</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.postal_code }}</dd>
            </div>
            <div v-if="company.country">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Country</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ company.country.name }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <!-- Subscription & Settings -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- Subscription Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Subscription
          </h3>
        </div>
        <div class="px-6 py-4">
          <dl class="space-y-4">
            <div v-if="company.subscription">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Plan</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ company.subscription.plan?.name || 'N/A' }}
              </dd>
            </div>
            <div v-if="company.subscription">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
              <dd class="mt-1">
                <span
                  :class="getSubscriptionStatusBadgeClass(company.subscription.status)"
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                >
                  {{ formatStatus(company.subscription.status) }}
                </span>
              </dd>
            </div>
            <div v-if="company.subscription">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Period</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ formatBillingPeriod(company.subscription.billing_period) }}
              </dd>
            </div>
            <div v-if="company.company_status === 'trial' && company.trial_ends_at">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Trial Ends</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ formatDate(company.trial_ends_at) }}
                <span class="text-yellow-600 dark:text-yellow-400 ml-2">
                  ({{ getTrialDaysRemaining(company.trial_ends_at) }})
                </span>
              </dd>
            </div>
            <div v-if="company.subscription && company.subscription.current_period_end">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Current Period Ends</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ formatDate(company.subscription.current_period_end) }}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- System Settings -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            System Settings
          </h3>
        </div>
        <div class="px-6 py-4">
          <dl class="space-y-4">
            <div v-if="company.currency">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Currency</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ company.currency.code }} - {{ company.currency.name }}
              </dd>
            </div>
            <div v-if="company.timezone">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Timezone</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ company.timezone.display_name || company.timezone.name }}
              </dd>
            </div>
            <div>
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Storage Quota</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ company.storage_quota_gb || 0 }} GB
              </dd>
            </div>
            <div v-if="company.payment_method">
              <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</dt>
              <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                {{ formatPaymentMethod(company.payment_method) }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <!-- Users -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
          Users ({{ company.users?.length || 0 }})
        </h3>
      </div>
      <div class="overflow-x-auto">
        <table v-if="company.users && company.users.length > 0" class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Email
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Joined
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="user in company.users" :key="user.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {{ user.first_name }} {{ user.last_name }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {{ user.email }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {{ formatDate(user.pivot?.joined_at || user.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          No users found.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import {
  PencilIcon,
  UserIcon,
  ArrowLeftIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
});

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Format status
const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
};

// Format billing period
const formatBillingPeriod = (period) => {
  if (!period) return 'N/A';
  return period.charAt(0).toUpperCase() + period.slice(1);
};

// Format payment method
const formatPaymentMethod = (method) => {
  if (!method) return 'N/A';
  const methods = {
    stripe: 'Stripe',
    bank_transfer: 'Bank Transfer',
    free: 'Free',
    zero_plan: 'Zero Plan',
  };
  return methods[method] || method;
};

// Get status badge class
const getStatusBadgeClass = (status) => {
  const classes = {
    active: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    trial: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    suspended: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  };
  return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

// Get subscription status badge class
const getSubscriptionStatusBadgeClass = (status) => {
  const classes = {
    active: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    trialing: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    past_due: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    expired: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
  };
  return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

// Get trial days remaining
const getTrialDaysRemaining = (trialEndsAt) => {
  const now = new Date();
  const endsAt = new Date(trialEndsAt);
  const daysRemaining = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) {
    return 'Expired';
  } else if (daysRemaining === 0) {
    return 'Ends today';
  } else if (daysRemaining === 1) {
    return '1 day left';
  } else {
    return `${daysRemaining} days left`;
  }
};
</script>
